/**
 * GenericModule — Page CRUD générique pour modules simples
 * Permet de lister, ajouter et supprimer des entités avec un schéma de formulaire déclaratif.
 */
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, RefreshCw } from "lucide-react";
import { api, formatDate, formatDateTime, formatXAF, formatNumber } from "@/lib/api";
import { useEntity } from "@/lib/entity";
import { useRealtime } from "@/lib/realtime";
import Modal, { ConfirmDialog } from "@/components/Modal";
import VoiceDictate from "@/components/VoiceDictate";

/**
 * Props:
 *  - title, subtitle, dataTestId
 *  - endpoint: string ex "/suppliers" → GET /suppliers/{entity} / POST /suppliers / DELETE /suppliers/{id}
 *  - extractItems: (data) => array (default: data.items || data)
 *  - extractStats: (data) => [{label, value, money?, count?, accent?}]
 *  - columns: [{key, label, render?: (row) => JSX, align?, money?, type?: 'date'|'datetime'|'pill'|'number'}]
 *  - fields: [{key, label, type: 'text'|'number'|'select'|'date'|'textarea'|'email'|'checkbox', options?, required?, placeholder?, group?: 1|2|3}]
 *  - defaults: object → initial form values
 *  - withEntity: bool (default true) — adds entity_id select in form
 *  - deletePerm: array of allowed roles (default null = all)
 *  - extraToolbar: optional JSX
 *  - rowActions: optional (row) => JSX (extra buttons in each row)
 */
export default function GenericModule({
  title,
  subtitle,
  dataTestId,
  endpoint,
  itemEndpoint = null, // defaults to endpoint
  extractItems = (d) => d.items || d,
  extractStats = () => [],
  columns,
  fields,
  defaults = {},
  withEntity = true,
  rowActions,
  emptyMessage = "Aucune donnée pour l'instant.",
  newButtonLabel = "Nouveau",
  customForm = null,
  headerExtra = null,
}) {
  const { currentEntityId, entities } = useEntity();
  const { dataChangeCounter } = useRealtime();
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [form, setForm] = useState(() => ({ ...defaults, entity_id: currentEntityId === "GROUPE" ? "ROPS" : currentEntityId }));

  const opEntities = entities.filter((e) => e.id !== "GROUPE");
  const items = extractItems(data) || [];
  const stats = extractStats(data) || [];

  useEffect(() => { load(); }, [currentEntityId]);
  // Re-fetch on any data_change broadcast from WebSocket (live collaboration)
  useEffect(() => {
    if (dataChangeCounter > 0) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataChangeCounter]);
  useEffect(() => {
    setForm({ ...defaults, entity_id: currentEntityId === "GROUPE" ? "ROPS" : currentEntityId });
  }, [currentEntityId]);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`${endpoint}/${currentEntityId}`);
      setData(data);
    } catch (e) {
      console.error("load error", e);
      setData({ items: [] });
    } finally { setLoading(false); }
  }

  async function submit(e) {
    e.preventDefault();
    try {
      // Convert numeric fields
      const payload = { ...form };
      fields.forEach(f => {
        if (f.type === "number" && payload[f.key] !== undefined && payload[f.key] !== "") {
          payload[f.key] = parseFloat(payload[f.key]);
        }
      });
      await api.post(itemEndpoint || endpoint, payload);
      setShowForm(false);
      setForm({ ...defaults, entity_id: currentEntityId === "GROUPE" ? "ROPS" : currentEntityId });
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur");
    }
  }

  async function remove(id) {
    try {
      await api.delete(`${itemEndpoint || endpoint}/${id}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur");
    }
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="space-y-6" data-testid={dataTestId}>
      {/* Stats */}
      {stats.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <Stat key={i} {...s} />
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">{title}</h2>
          {subtitle && <p className="text-xs text-obsidian-400 mt-1">{subtitle} · {items.length} entrée(s)</p>}
        </div>
        <div className="flex gap-2">
          {headerExtra}
          <button onClick={load} className="btn-secondary" title="Rafraîchir"><RefreshCw size={14} /></button>
          <button onClick={() => setShowForm(true)} className="btn-primary" data-testid={`${dataTestId}-new-button`}>
            <Plus size={14} /> {newButtonLabel}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-x-auto">
        <table className="w-full text-sm min-w-max">
          <thead>
            <tr className="text-obsidian-400 border-b border-line">
              {columns.map((c) => (
                <th key={c.key} className={`overline px-4 py-3 text-${c.align || "left"}`}>{c.label}</th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={columns.length + 1} className="p-8 text-center text-obsidian-500">Chargement…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="p-12 text-center text-obsidian-500">
                {emptyMessage}<br/>
                <button onClick={() => setShowForm(true)} className="mt-3 text-citron hover:underline text-sm">+ Ajouter le premier</button>
              </td></tr>
            )}
            {items.map((row) => (
              <tr key={row.id} className="border-b border-line/50 hover:bg-obsidian-850 transition-colors" data-testid={`${dataTestId}-row`}>
                {columns.map((c) => (
                  <td key={c.key} className={`px-4 py-3 text-${c.align || "left"}`}>
                    {renderCell(row, c)}
                  </td>
                ))}
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    {rowActions && rowActions(row, { reload: load })}
                    <button onClick={() => setConfirmId(row.id)} className="btn-ghost h-7 w-7 p-0 text-obsidian-500 hover:text-bad" data-testid={`${dataTestId}-delete`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Form modal */}
      <Modal open={showForm} onClose={() => setShowForm(false)} title={newButtonLabel} subtitle={subtitle} size="lg">
        {customForm ? customForm({ form, setForm, submit, close: () => setShowForm(false) }) : (
          <form onSubmit={submit} className="space-y-4" data-testid={`${dataTestId}-form`}>
            {withEntity && (
              <div>
                <label className="label block">Entité</label>
                <select className="input" value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} required>
                  {opEntities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </div>
            )}
            {groupFields(fields).map((group, gi) => (
              <div key={gi} className={group.length > 1 ? `grid grid-cols-${Math.min(group.length, 3)} gap-3` : ""}>
                {group.map((f) => <FormField key={f.key} field={f} form={form} setForm={setForm} />)}
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Annuler</button>
              <button type="submit" className="btn-primary" data-testid={`${dataTestId}-submit`}>Enregistrer</button>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={!!confirmId}
        onClose={() => setConfirmId(null)}
        onConfirm={() => remove(confirmId)}
        title="Supprimer cet élément ?"
        message="Cette action est irréversible et sera tracée."
        confirmLabel="Supprimer"
      />
    </motion.div>
  );
}

function groupFields(fields) {
  // Group consecutive fields with the same `group` value
  const groups = [];
  let current = [];
  let lastGroup = null;
  fields.forEach((f) => {
    const g = f.group || null;
    if (g === lastGroup && g !== null && current.length < 3) {
      current.push(f);
    } else {
      if (current.length) groups.push(current);
      current = [f];
      lastGroup = g;
    }
  });
  if (current.length) groups.push(current);
  return groups;
}

function FormField({ field: f, form, setForm }) {
  const v = form[f.key] ?? "";
  const set = (val) => setForm({ ...form, [f.key]: val });
  return (
    <div className="flex-1">
      <label className="label block flex items-center justify-between gap-2">
        <span>{f.label}{f.required && " *"}</span>
        {f.type === "textarea" && f.voice && (
          <VoiceDictate
            onTranscript={(t) => set(((v || "") + (v ? " " : "") + t).trim())}
            title="Dicter dans ce champ"
          />
        )}
      </label>
      {f.type === "select" && (
        <select className="input" required={f.required} value={v} onChange={(e) => set(e.target.value)}>
          {!v && <option value="">— Choisir —</option>}
          {f.options.map((o) => typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
      {f.type === "textarea" && (
        <textarea className="input min-h-[80px] py-2" rows={3} required={f.required} placeholder={f.placeholder} value={v} onChange={(e) => set(e.target.value)} />
      )}
      {f.type === "checkbox" && (
        <label className="flex items-center gap-2 h-10 px-3 bg-obsidian-900 border border-line rounded-sm cursor-pointer">
          <input type="checkbox" checked={!!v} onChange={(e) => set(e.target.checked)} className="accent-citron" />
          <span className="text-sm">{f.placeholder || f.label}</span>
        </label>
      )}
      {!["select", "textarea", "checkbox"].includes(f.type) && (
        <input
          type={f.type || "text"}
          className={`input ${f.type === "number" ? "font-mono" : ""}`}
          required={f.required}
          placeholder={f.placeholder}
          min={f.type === "number" ? 0 : undefined}
          step={f.type === "number" ? f.step || "1" : undefined}
          value={v}
          onChange={(e) => set(e.target.value)}
        />
      )}
    </div>
  );
}

function renderCell(row, c) {
  const v = row[c.key];
  if (c.render) return c.render(row);
  if (c.type === "money") return <span className="font-mono tabular">{formatXAF(v)}</span>;
  if (c.type === "number") return <span className="font-mono tabular">{formatNumber(v, c.digits || 0)}</span>;
  if (c.type === "date") return <span className="font-mono text-xs">{formatDate(v)}</span>;
  if (c.type === "datetime") return <span className="font-mono text-xs">{formatDateTime(v)}</span>;
  if (c.type === "pill") return <span className={`pill ${c.pillClass?.(row) || "bg-obsidian-800"}`}>{v}</span>;
  if (c.type === "code") return <span className="font-mono text-xs text-obsidian-300">{v}</span>;
  if (c.muted) return <span className="text-obsidian-400 text-xs">{v || "—"}</span>;
  return v ?? "—";
}

function Stat({ label, value, money, count, accent, highlight }) {
  const colors = { ok: "text-ok", warn: "text-warn", citron: "text-citron", bad: "text-bad" };
  return (
    <div className={`card p-5 ${highlight ? "ring-1 ring-citron/30" : ""}`}>
      <div className="overline mb-3">{label}</div>
      <div className={`font-mono text-2xl font-semibold tabular ${colors[accent] || "text-obsidian-100"}`}>
        {money ? formatXAF(value) : count ? formatNumber(value) : value}
      </div>
    </div>
  );
}
